import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { API_ENDPOINT } from '../constants';
import { Channel, GoonAPIClient, Post, User } from '../client/ApiClient';
import { consume } from '@lit/context';
import { authContext, AuthState } from '../context/AuthContext';
import { isLikelyUrl } from '../utils/url';

const api = new GoonAPIClient(API_ENDPOINT);

@customElement('channel-preview')
export class ChannelPreview extends LitElement {
  createRenderRoot() { return this }

  @property({ type: Array }) posts: Post[] | null = null;
  @property({ type: Array }) creators: User[] | null = null;
  @property({ type: String }) channelslug: string | null = null;
  @property({ type: Object }) channel: Channel | null = null;

  @state() followingProfileIds: Set<string> = new Set();

  @consume({ context: authContext, subscribe: true })
  auth!: AuthState;

  async connectedCallback() {
    super.connectedCallback();
    try {
      this.channel = await api.getChannelBySlug(this.channelslug);
      const res = await api.getPaginatedChannelPosts(this.channelslug, 0, 'hot', 'year');
      this.posts = res.posts;
      this.creators = res.users;
      if (this.auth.userId) {
        const following = await api.getFollowingProfiles();
        this.followingProfileIds = new Set(following.following.map(p => p.id));
      }
    } catch (err) {
      console.error('Failed to load channel preview:', err);
    }
  }

  async checkAuthOrPrompt() {
    if (!this.auth.userId) {
      this.dispatchEvent(new CustomEvent('open-auth-modal', { bubbles: true, composed: true }));
      return false;
    }
    return true;
  }

  async handleFollowToggle(creatorId: string) {
    if (!(await this.checkAuthOrPrompt())) return;
    if (this.followingProfileIds.has(creatorId)) {
      await api.unfollowUser(creatorId);
      this.followingProfileIds.delete(creatorId);
    } else {
      await api.followUser(creatorId);
      this.followingProfileIds.add(creatorId);
    }
    this.requestUpdate();
  }

  handlePostDeleted(e: CustomEvent) {
    const idToRemove = e.detail.postId;
    this.posts = this.posts.filter(p => p.id !== idToRemove);
  }

  render() {
    const channelImg = this.channel?.image_url || '/images/placeholder.png';

    return this.posts ? html`
      <div class="card bg-base-300 shadow-xl transition-all duration-300 ease-in-out hover:shadow-2xl">
        <div class="card-body space-y-6">
          <a href="/channel/${this.channelslug}" class="flex items-center gap-4 border-b border-base-300 pb-4">
            <img class="w-14 h-14 rounded-full object-cover ring ring-primary ring-offset-2" src=${channelImg} alt="Channel image" />
            <h3 class="text-2xl font-bold text-primary hover:underline transition-colors duration-200">#${this.channel?.name || this.channelslug}</h3>
          </a>

          <ul class="space-y-6">
            ${this.posts.slice(0, 5).map(post => {
              const creator = this.creators?.find(c => c.id === post.user_profile_id);
              if (!creator) return null;

              return html`
              <post-card
                .post=${post}
                .creator=${creator}
                @post-deleted=${this.handlePostDeleted}
              ></post-card>
              `;
            })}
          </ul>

          <div class="pt-2 flex justify-end">
            <a class="btn btn-outline btn-accent group" href="/channel/${this.channelslug}" data-ph-capture-attribute-channel="${this.channelslug}">
              <span>More</span>
              <svg class="ml-1 w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    ` : null;
  }
}

